import { 
    Button, 
    Dialog, 
    DialogContent, 
    DialogTitle,
    DialogActions,
    TextField,
    Typography,
    Box,
    Stack,
    Alert,
    IconButton,
    Chip,
    Divider
} from "@mui/material"
import {
    Close as CloseIcon,
    Create as CreateIcon,
    Image as ImageIcon,
    Send as SendIcon
} from "@mui/icons-material"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { createComment } from "../../controllers/posts/comments"
import { PostMedia } from "./card/PostMedia"
import { useState } from "react"

export const CreatePost = ({token, open, setOpen, status, setStatus, buttonTitle, visibility}) => {
    const [media_ids, setMediaIds] = useState([])
    const [imageUrls, setImageUrls] = useState([])
    const [postContent, setPostContent] = useState('')
    const [errors, setErrors] = useState({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    
    const queryClient = useQueryClient()
    const mutation = useMutation({
        mutationFn: createComment,
        onSuccess: () => {
            // Invalidate and refetch
            queryClient.invalidateQueries({ queryKey: ['homeTimeline'] })
            queryClient.invalidateQueries({ queryKey: ['getConversations'] })
            setIsSubmitting(false)
            handleClose()
        },
        onError: (error) => {
            setIsSubmitting(false)
            console.error('Error creating post:', error)
        }
    })

    const validateForm = () => {
        const newErrors = {}
        const fullContent = ((status || '') + " " + postContent).trim()
        
        if (!fullContent || fullContent.length < 3) {
            newErrors.content = 'Post content is required (at least 3 characters)'
        }
        
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleClose = () => {
        setOpen(false)
        setPostContent('')
        setErrors({})
        setIsSubmitting(false)
    }

    const send_reply = () => {
        if (!validateForm()) return
        
        setIsSubmitting(true)
        mutation.mutate({
            body: {
                status: ((status || '') + " " + postContent).trim(),
                visibility,
                media_ids
            },
            token
        })
    }
    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="create-post-dialog-title"
            aria-describedby="create-post-dialog-description"
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    minHeight: '400px'
                }
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CreateIcon color="primary" />
                        <Typography variant="h6" component="h2" fontWeight="bold">
                            {buttonTitle || 'Create Post'}
                        </Typography>
                    </Box>
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                
                {/* Visibility Indicator */}
                <Box sx={{ mt: 1 }}>
                    <Chip 
                        size="small" 
                        label={visibility === 'public' ? '🌍 Public' : '🔒 Private'} 
                        color={visibility === 'public' ? 'success' : 'default'}
                        variant="outlined"
                    />
                </Box>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ pt: 3 }}>
                <Stack spacing={3}>
                    {/* Pre-filled Status */}
                    {status && (
                        <Box sx={{ 
                            p: 2, 
                            bgcolor: 'grey.50', 
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'grey.200'
                        }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Context:
                            </Typography>
                            <Typography variant="body1">
                                {status}
                            </Typography>
                        </Box>
                    )}

                    {/* Main Content Input */}
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={postContent}
                        onChange={(e) => setPostContent(e.target.value)}
                        label={status ? "Add your thoughts..." : (buttonTitle || "What's on your mind?")}
                        variant="outlined"
                        placeholder={status ? "Continue the conversation..." : "Share what you're thinking about..."}
                        error={!!errors.content}
                        helperText={errors.content}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                            }
                        }}
                    />

                    {/* Character Count */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                            {((status || '') + " " + postContent).trim().length} characters
                        </Typography>
                        {((status || '') + " " + postContent).trim().length > 280 && (
                            <Typography variant="caption" color="warning.main">
                                Long posts may be truncated in some feeds
                            </Typography>
                        )}
                    </Box>

                    {/* Media Upload Section */}
                    <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ImageIcon color="primary" fontSize="small" />
                            Add Images (Optional)
                        </Typography>
                        <PostMedia 
                            imageUrls={imageUrls} 
                            setImageUrls={setImageUrls} 
                            media_ids={media_ids} 
                            setMediaIds={setMediaIds} 
                            token={token} 
                        />
                    </Box>

                    {/* Error Alert */}
                    {Object.keys(errors).length > 0 && (
                        <Alert severity="error" sx={{ borderRadius: 2 }}>
                            Please fix the errors above before posting.
                        </Alert>
                    )}
                </Stack>
            </DialogContent>

            <Divider />

            <DialogActions sx={{ p: 3, pt: 2 }}>
                <Button 
                    onClick={handleClose} 
                    color="inherit"
                    sx={{ borderRadius: 2 }}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={send_reply} 
                    variant="contained" 
                    disabled={isSubmitting || (!postContent.trim() && !status)}
                    startIcon={isSubmitting ? null : <SendIcon />}
                    sx={{
                        borderRadius: 2,
                        px: 3,
                        textTransform: 'none',
                        fontWeight: 'bold'
                    }}
                >
                    {isSubmitting ? 'Posting...' : (buttonTitle || 'Post')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}